import { type ComponentProps, useEffect, useRef, useState, type ReactNode } from 'react';
import { useRouter } from 'next/router';
import { ChevronDown } from 'lucide-react';
import type { WebGoal } from '../../../lib/goals';
import { createGoalContext, findGoal, newGoalSeedKey, useWorkspaceGoals } from '../../../lib/goals';
import { useWorkspaces } from '../../../lib/workspaces';
import { useSessionSpaceFilter } from '../../../lib/use-session-space-filter';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useLoadBi } from '../../../lib/use-load-bi';
import { useOpenSideChat } from '../../../lib/use-open-side-chat';
import { useTranslation } from '../../../lib/i18n';
import { GOAL_LIST_GROUP_BADGE, GOAL_LIST_GROUP_TITLE_KEYS, goalListSections, type GoalListGroup, type GoalListSection } from '../../../lib/goal-list-sections';
import { spacesOf, type FilterOption } from '../../../lib/task-filters';
import { useTaskSidePanel } from '../task-side-panel/TaskSidePanelContext';
import { Badge, Button, OverflowMenu, type OverflowMenuAction } from '../ui';
import { CategoryFilterMenu, useSpaceMark } from '../category-view/CategoryFilterMenu';
import {
  CategoryHeader,
  CategoryList,
  CategoryListGroupHeader,
  CategoryPage,
  CategoryToolbar,
  CategoryToolbarLabel,
  ClearFiltersButton,
} from '../category-view/CategoryView';
import { GoalCard } from './GoalCard';
import { LiveGoalDetailsModal } from './GoalDetailsModal';
import styles from './GoalsBody.module.css';
import { useCategoryRedesign } from '../../../lib/use-category-redesign';
import { GoalsBody as GoalsBodyLegacy } from './GoalsBody.legacy';

// THE goals renderer — grid + create CTA, self-contained (fetches its own data). Mirrors `TasksBody`'s
// two scoping modes so the same component serves the top-level Goals screen AND a Space's Dashboard tile
// drill-in:
//   - ACCOUNT-WIDE (no workspaceId, or `filterable` with none selected) — the top-level `/goals` screen.
//   - PER-WORKSPACE (workspaceId, no `filterable`) — a host that has already fixed its scope.
//
// `filterable` adds a Space picker (`TasksFilterBar`'s single-axis sibling) and changes what `workspaceId`
// MEANS: with it, the list is always fetched account-wide and `workspaceId` is the SELECTED Space (read off
// `/goals?workspace=<slug>` by the page), so a reader can widen back to every Space. Without it,
// `workspaceId` is a host scope applied server-side, exactly as the original workspace Goals tab did.
function GoalsBodyRedesign({
  workspaceId,
  header,
  filterable: filterableProp,
  page = 'goals',
  onHome,
  onSpaceChange,
  action,
}: {
  workspaceId?: string;
  header?: ReactNode;
  filterable?: boolean;
  page?: string;
  /** Hosted by the home's Goals category: account-wide and filterable, with the Space pick held here
   *  rather than in the URL, and no "+ New goal" of its own — the home draws it (`Home3CategoryView`). */
  onHome?: boolean;
  /** The home's Space pick, reported so the host's create button can scope its seed to it. */
  onSpaceChange?: (workspaceId: string) => void;
  /** The home's create button, at the toolbar's end. */
  action?: ReactNode;
}) {
  const { t } = useTranslation();
  const { openModal } = useTaskSidePanel();
  const { logPageClickBi } = useGenericBi();
  const openSideChat = useOpenSideChat();
  const router = useRouter();
  const filterable = filterableProp || onHome;
  const [homeSpace, setHomeSpace] = useState('');
  // The toolbar's status picker: one of the list's own groups, or '' for all of them. A view option over
  // the groups the list already draws — never a status the list has no section for.
  const [groupPick, setGroupPick] = useState<GoalListGroup | ''>('');
  const workspacesState = useWorkspaces();
  const fullRoster = workspacesState.status === 'ready' ? workspacesState.workspaces : [];
  const workspaceRoster: FilterOption[] = fullRoster;

  const state = useWorkspaceGoals(filterable ? undefined : workspaceId);
  useLoadBi('goals-data', { isLoading: state.status === 'loading', isError: state.status === 'error' });

  const spaceOptions = filterable && state.status === 'ready' ? spacesOf(state.goals, workspaceRoster) : [];
  // A standalone page holds its pick for the visit only (`useSessionSpaceFilter`).
  const [pageSpace, setPageSpace] = useSessionSpaceFilter(workspaceId, !!filterableProp && !onHome);
  const selectedId = onHome ? homeSpace : filterable ? pageSpace : '';
  const goals = filterable && state.status === 'ready' ? state.goals.filter((goal) => !selectedId || goal.workspaceId === selectedId) : state.status === 'ready' ? state.goals : [];
  // The Space actually in scope for the create-goal seed: the FILTER pick when filterable, else the
  // host's own fixed `workspaceId` — never `selectedId`, which is always '' in the non-filterable mode.
  const scopeId = filterable ? selectedId : workspaceId;
  const selectedName = workspaceRoster.find((w) => w.id === scopeId)?.name;

  const applyFilter = (nextId: string) => {
    logPageClickBi({ page_name: page, button_name: nextId ? 'filter_space' : 'filter_space_all' });
    if (onHome) {
      setHomeSpace(nextId);
      onSpaceChange?.(nextId);
      return;
    }
    setPageSpace(nextId);
  };

  const deepLinkedGoalId = typeof router.query.goal === 'string' ? router.query.goal : '';
  const openedDeepLink = useRef('');

  const openGoal = (goal: WebGoal) => {
    logPageClickBi({ page_name: page, button_name: 'open_goal', answer: goal.id });
    openModal(<LiveGoalDetailsModal workspaceId={goal.workspaceId} goalId={goal.id} initialGoal={goal} />, goal.id);
  };

  // `workspace/<slug>/goal/<goalId>` lands here as `?goal=<goalId>` (see lib/app-link.ts) — open that
  // goal once the list it lives in has loaded. Guarded on the id already opened rather than a bare
  // boolean so a second deep link in the same session still opens, while a re-render does not reopen
  // a modal the user has closed.
  useEffect(() => {
    if (!deepLinkedGoalId || state.status !== 'ready' || openedDeepLink.current === deepLinkedGoalId) {
      return;
    }
    const goal = findGoal(state.goals, deepLinkedGoalId);
    if (!goal) {
      return;
    }
    openedDeepLink.current = deepLinkedGoalId;
    openModal(<LiveGoalDetailsModal workspaceId={goal.workspaceId} goalId={goal.id} initialGoal={goal} />, goal.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLinkedGoalId, state]);

  // The empty state's "Create a goal" CTA: the unscoped seed, same key and copy as native's
  // GenericEmptyState CTA (iOS `GoalsTab.createGoal`, Android `GoalsList onCreateGoal`).
  const createGoal = () => {
    logPageClickBi({ page_name: page, button_name: 'create_goal' });
    openSideChat({ kind: 'new', message: t('goals.createPrompt') });
  };

  // The populated list's "+ New goal" affordance, scoped to the SELECTED Space when there is one (a host
  // scope, or a filter pick) — unscoped otherwise, matching the account-wide screen with nothing narrowed.
  const createGoalScoped = () => {
    logPageClickBi({ page_name: page, button_name: 'create_goal' });
    const name = (selectedName ?? '').trim();
    openSideChat({
      kind: 'new',
      message: t(newGoalSeedKey(scopeId ? name : undefined), { workspace: name }),
      context: createGoalContext(scopeId, name),
    });
  };

  const sections = goalListSections(goals);
  const pickedGroup = sections.some((section) => section.group === groupPick) ? groupPick : '';
  const shownSections = pickedGroup ? sections.filter((section) => section.group === pickedGroup) : sections;
  const pickGroup = (next: GoalListGroup | '') => {
    logPageClickBi({ page_name: page, button_name: next ? 'filter_goal_status' : 'filter_goal_status_all', state: next || 'all' });
    setGroupPick(next);
  };

  return (
    <div className={styles.live}>
      {header}
      <CategoryPage>
        {/* The home names the category itself; a standalone page's PageHeader already does. */}
        <CategoryHeader
          title={onHome ? t('categoryView.goals.title') : undefined}
          description={onHome ? t('categoryView.goals.description') : undefined}
          filters={
            (filterable && spaceOptions.length > 0) || pickedGroup ? (
              <SpaceFilter
                options={filterable ? spaceOptions : []}
                selectedId={selectedId}
                onChange={applyFilter}
                statusPicked={!!pickedGroup}
                onClearStatus={() => pickGroup('')}
              />
            ) : undefined
          }
        />
        {state.status === 'error' && <div className={styles.error}>{t('goals.error')}</div>}
        {state.status === 'loading' && (
          <div className={styles.grid}>
            {[0, 1, 2].map((i) => (
              <div key={i} className={styles.skeleton} aria-hidden />
            ))}
          </div>
        )}
        {state.status === 'ready' &&
          (goals.length === 0 ? (
            <div className={styles.empty}>
              <div className={styles.emptyTitle}>{t('goals.empty.title')}</div>
              <div className={styles.emptyBody}>{t('goals.empty.body')}</div>
              <div className={styles.emptyCta}>
                <Button onClick={createGoal}>{t('goals.empty.cta')}</Button>
              </div>
            </div>
          ) : (
            <>
              <CategoryToolbar
                end={<GroupFilter sections={sections} picked={pickedGroup} onChange={pickGroup} />}
                action={
                  onHome ? (
                    action
                  ) : (
                    <Button onClick={createGoalScoped}>
                      {t('goals.create.cta')}
                    </Button>
                  )
                }
              >
                <CategoryToolbarLabel>{t('categoryView.allGoals')}</CategoryToolbarLabel>
              </CategoryToolbar>
              <CategoryList>
                {shownSections.map((section) => (
                  <GoalSection key={section.group} section={section} onOpen={openGoal} />
                ))}
              </CategoryList>
            </>
          ))}
      </CategoryPage>
    </div>
  );
}

// One group of the Goals screen: a header row ("In Progress · 2") and its goals as rows of the list card.
// "Older" starts folded behind its own header, so a long history never pushes the goals in progress off
// the screen; every other group is open. The goals inside keep the server's order.
function GoalSection({ section, onOpen }: { section: GoalListSection; onOpen: (goal: WebGoal) => void }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(!section.collapsedByDefault);
  const title = `${t(GOAL_LIST_GROUP_TITLE_KEYS[section.group])} · ${section.goals.length}`;
  return (
    <>
      <CategoryListGroupHeader>
        {section.collapsedByDefault ? (
          <Button
            variant="text"
            size="small"
            className={styles.sectionToggle}
            aria-expanded={open}
            endIcon={<ChevronDown aria-hidden size={16} className={open ? styles.chevronOpen : undefined} />}
            onClick={() => setOpen((value) => !value)}
          >
            {title}
          </Button>
        ) : (
          title
        )}
      </CategoryListGroupHeader>
      {open &&
        section.goals.map((goal) => (
          <GoalCard key={goal.id} goal={goal} groupBadge={GOAL_LIST_GROUP_BADGE[section.group]} onOpen={() => onOpen(goal)} />
        ))}
    </>
  );
}

function GroupFilter({
  sections,
  picked,
  onChange,
}: {
  sections: GoalListSection[];
  picked: GoalListGroup | '';
  onChange: (group: GoalListGroup | '') => void;
}) {
  const { t } = useTranslation();
  const allLabel = t('categoryView.goalStatus.all');
  const label = picked ? t(GOAL_LIST_GROUP_TITLE_KEYS[picked]) : allLabel;
  // Each status reads as the badge its goals wear in the list (Figma 21123:81201), so the menu and the
  // rows speak one vocabulary.
  const actions: OverflowMenuAction[] = [
    { key: 'all', label: allLabel, onSelect: () => onChange('') },
    ...sections.map((section) => ({
      key: section.group,
      label: <Badge variant={GOAL_LIST_GROUP_BADGE[section.group].variant} label={t(GOAL_LIST_GROUP_BADGE[section.group].labelKey)} />,
      onSelect: () => onChange(section.group),
    })),
  ];
  return (
    <OverflowMenu
      label={label}
      ariaLabel={t('categoryView.allGoals')}
      actions={actions}
      selectedKey={picked || 'all'}
      triggerVariant="text"
      triggerSize="small"
      align="end"
    />
  );
}

/** The header's Space menu, then the ONE "Clear filters · N" for the page — which also counts and clears the
 *  status the toolbar's menu picked, so no narrowing is left behind a clear. */
function SpaceFilter({
  options,
  selectedId,
  onChange,
  statusPicked,
  onClearStatus,
}: {
  options: FilterOption[];
  selectedId: string;
  onChange: (id: string) => void;
  statusPicked: boolean;
  onClearStatus: () => void;
}) {
  const { t } = useTranslation();
  const spaceMark = useSpaceMark();
  return (
    <div className={styles.filters}>
      {options.length > 0 && (
        <CategoryFilterMenu
          options={options.map((option) => ({ id: option.id, name: option.name, icon: spaceMark(option.id, option.name) }))}
          selectedId={selectedId}
          allLabel={t('tasks.filter.allSpaces')}
          ariaLabel={t('packs.section.workspace')}
          onPick={onChange}
        />
      )}
      <ClearFiltersButton
        count={(selectedId ? 1 : 0) + (statusPicked ? 1 : 0)}
        onClear={() => {
          if (selectedId) onChange('');
          if (statusPicked) onClearStatus();
        }}
      />
    </div>
  );
}

/** The redesign rides the activity-map home's release (`lib/use-category-redesign.ts`); off, this is the legacy design. */
export function GoalsBody(props: ComponentProps<typeof GoalsBodyRedesign>) {
  const redesign = useCategoryRedesign();
  if (!redesign.resolved) return null;
  return redesign.on ? <GoalsBodyRedesign {...props} /> : <GoalsBodyLegacy {...props} />;
}
